/**
 * SimuladorReport — full per-session coaching report.
 *
 * Uses unified ConsultaReportCore schema from @/lib/analysis/types.
 * Renders: hero → método 6 etapas → flags → gargalo → ações → narrativa + prática → transcrição.
 */
import { useRef, useState } from "react";
import {
  CheckCircle,
  XCircle,
  Clock,
  Sparkle,
  Warning,
  Lightning,
  Trophy,
  Lightbulb,
  ListNumbers,
  Target,
  CaretDown,
  ChatCircle,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type {
  AnalysisGrade,
  AcaoCorretiva,
  EneatypeAdaptation,
  EneatypeDetected,
  FlagCritica,
  Gargalo,
  MethodStagesUnified,
  NarrativeItem,
  SimulatorReport,
} from "@/lib/analysis/types";
import { cn } from "@/lib/utils";

// ─── Stage order / labels ──────────────────────────────────────────────────────

const STAGE_ORDER: Array<keyof MethodStagesUnified> = [
  "conexao",
  "perguntas_iniciais",
  "investigacao_dor",
  "diagnostico_clinico",
  "oferta",
  "fechamento",
];

const STAGE_LABELS: Record<keyof MethodStagesUnified, string> = {
  conexao:             "1 · Conexão e Autoridade",
  perguntas_iniciais:  "2 · 3 Perguntas Iniciais",
  investigacao_dor:    "3 · Investigação de Dor",
  diagnostico_clinico: "4 · Diagnóstico Clínico",
  oferta:              "5 · Oferta",
  fechamento:          "6 · Fechamento",
};

const ETAPA_SHORT: Record<string, string> = {
  conexao:             "Conexão",
  perguntas_iniciais:  "Perguntas",
  investigacao_dor:    "Investigação",
  diagnostico_clinico: "Diagnóstico",
  oferta:              "Oferta",
  fechamento:          "Fechamento",
  geral:               "Geral",
};

// ─── Grade meta ───────────────────────────────────────────────────────────────

const GRADE_META: Record<AnalysisGrade, { label: string; cls: string }> = {
  excelente:    { label: "Excelente",    cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  bom:          { label: "Bom",          cls: "text-sky-400    bg-sky-500/10    border-sky-500/30"    },
  intermediario:{ label: "Treinar",      cls: "text-amber-400  bg-amber-500/10  border-amber-500/30"  },
  fraco:        { label: "Fraco",        cls: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  critico:      { label: "Recomeçar",    cls: "text-rose-400   bg-rose-500/10   border-rose-500/30"   },
};

// ─── Key-moment display ───────────────────────────────────────────────────────

const KEY_MOMENT_ICON = {
  positive:    CheckCircle,
  objection:   Warning,
  opportunity: Sparkle,
  error:       XCircle,
  close:       Trophy,
  stall:       Clock,
} as const;

const KEY_MOMENT_TONE: Record<SimulatorReport["key_moments"][number]["type"], string> = {
  positive:    "text-emerald-400",
  objection:   "text-amber-400",
  opportunity: "text-sky-400",
  error:       "text-rose-400",
  close:       "text-primary",
  stall:       "text-violet-400",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stageColors(score: number): { bar: string; text: string } {
  if (score >= 8) return { bar: "bg-emerald-500", text: "text-emerald-400" };
  if (score >= 5) return { bar: "bg-amber-500",   text: "text-amber-400"  };
  return               { bar: "bg-rose-500",   text: "text-rose-400"   };
}

function extractText(item: string | NarrativeItem): string {
  return typeof item === "string" ? item : item.text;
}

function extractEtapa(item: string | NarrativeItem): string | null {
  return typeof item === "string" ? null : item.etapa ?? null;
}

function extractSeq(item: string | NarrativeItem): number | null {
  return typeof item === "string" ? null : (item.seq ?? null);
}

// ─── Small atoms ──────────────────────────────────────────────────────────────

function EtapaTag({ etapa, className }: { etapa: string; className?: string }) {
  return (
    <span className={cn(
      "inline-flex items-center text-[10px] font-mono text-muted-foreground/70 bg-muted/30 px-1.5 py-0.5 rounded border border-border/30 whitespace-nowrap shrink-0",
      className
    )}>
      {ETAPA_SHORT[etapa] ?? etapa}
    </span>
  );
}

// ─── StageRow (collapsible) ───────────────────────────────────────────────────

interface StageRowProps {
  slug:       keyof MethodStagesUnified;
  label:      string;
  stageEval:  MethodStagesUnified[keyof MethodStagesUnified];
  isCritical: boolean;
  index:      number;
}

function StageRow({ slug: _slug, label, stageEval, isCritical, index }: StageRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { bar, text } = stageColors(stageEval.score);
  const pct = (stageEval.score / 10) * 100;
  const hasDetail =
    stageEval.criterios_atingidos.length > 0 ||
    stageEval.criterios_perdidos.length > 0 ||
    Boolean(stageEval.evidencia);

  return (
    <div
      className={cn(
        "rounded-lg border border-border/40 overflow-hidden",
        isCritical && "border-amber-500/40 bg-amber-500/5",
      )}
    >
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((e) => !e)}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 text-left transition",
          hasDetail && "hover:bg-muted/20 cursor-pointer",
          !hasDetail && "cursor-default",
        )}
      >
        <span className="text-xs font-mono text-muted-foreground w-4 shrink-0 select-none">
          {index + 1}
        </span>
        <span className="text-sm font-medium flex-1 min-w-0">
          <span className={isCritical ? "text-amber-300" : "text-foreground"}>{label}</span>
          {isCritical && (
            <span className="ml-2 text-[10px] font-normal text-amber-400/80 uppercase tracking-wider">
              etapa crítica
            </span>
          )}
        </span>
        <div className="w-16 h-1.5 bg-muted/40 rounded-full overflow-hidden shrink-0">
          <div
            className={cn("h-full rounded-full transition-all", bar)}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className={cn("text-sm font-bold tabular-nums w-9 text-right shrink-0", text)}>
          {stageEval.score}/10
        </span>
        {hasDetail ? (
          <CaretDown
            weight="bold"
            className={cn(
              "w-3 h-3 text-muted-foreground transition-transform duration-150 shrink-0",
              expanded && "rotate-180",
            )}
          />
        ) : (
          <span className="w-3 shrink-0" />
        )}
      </button>

      {expanded && hasDetail && (
        <div className="px-4 pb-3 border-t border-border/30 pt-3 space-y-2.5">
          {stageEval.criterios_atingidos.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1.5 font-medium">
                Atingido
              </p>
              <ul className="space-y-1">
                {stageEval.criterios_atingidos.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle weight="fill" className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {stageEval.criterios_perdidos.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-rose-400 mb-1.5 font-medium">
                Ausente / Fraco
              </p>
              <ul className="space-y-1">
                {stageEval.criterios_perdidos.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <XCircle weight="fill" className="w-3 h-3 text-rose-400 mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Evidência block */}
          {stageEval.evidencia && (
            <div className="mt-2 border-l-2 border-border/40 pl-3 bg-muted/20 rounded-r py-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-0.5">Evidência</p>
              <p className="text-xs text-foreground/80 italic leading-relaxed">"{stageEval.evidencia}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MethodStagesSection ──────────────────────────────────────────────────────

function MethodStagesSection({ report }: { report: SimulatorReport }) {
  if (!report.method_stages) return null;

  const total     = report.method_total ?? 0;
  const grade     = report.grade;
  const gradeMeta = grade ? GRADE_META[grade] : null;
  const critSlug  = report.etapa_critica;

  return (
    <Card className="p-6 bg-card/80 border-border/50">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <ListNumbers weight="duotone" className="w-5 h-5 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">
            Método Influência Máxima — 6 Etapas
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xl font-bold tabular-nums text-primary" data-testid="method-total">
            {total}
            <span className="text-sm font-normal text-muted-foreground">/60</span>
          </span>
          {gradeMeta && (
            <Badge className={cn("border font-semibold", gradeMeta.cls)}>
              {gradeMeta.label}
            </Badge>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {STAGE_ORDER.map((slug, i) => (
          <StageRow
            key={slug}
            slug={slug}
            label={STAGE_LABELS[slug]}
            stageEval={report.method_stages![slug]}
            isCritical={critSlug === slug}
            index={i}
          />
        ))}
      </div>
    </Card>
  );
}

// ─── FlagsDetectadasBlock ─────────────────────────────────────────────────────

const FLAG_LABELS: Record<string, string> = {
  PULO_DESEJO:       "Pulou direto para a oferta sem despertar desejo",
  ABERTURA_GENERICA: "Abertura sem personalização nem autoridade",
  SEM_SIGNIFICADO:   "Não conectou o procedimento ao significado emocional",
  DOMINANCIA_PERDIDA:"Perdeu o controle da condução da consulta",
  URGENCIA_INVENTADA:"Criou urgência artificial sem embasamento real",
  MICRO_SOLUCAO:     "Ofereceu solução pequena demais para o problema apresentado",
  PRECO_PRECOCE:     "Mencionou o preço antes de construir o valor",
  PROLIXIDADE:       "Falou demais e perdeu a atenção do paciente",
};

function FlagsDetectadasBlock({ flags }: { flags: FlagCritica[] }) {
  const detected = flags.filter(f => f.detectada);
  if (detected.length === 0) return null;

  return (
    <Card className="p-4 bg-rose-500/5 border-2 border-rose-500/40">
      <header className="mb-3 flex items-center gap-2">
        <Warning weight="fill" className="w-5 h-5 text-rose-400" />
        <h3 className="text-sm font-semibold text-rose-300">Alertas que custaram a venda</h3>
        <span className="ml-auto text-xs bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-full px-2 py-0.5">
          {detected.length}
        </span>
      </header>
      <div className="space-y-2">
        {detected
          .sort((a, b) => {
            const order = { alto: 0, medio: 1, baixo: 2 };
            return order[a.impacto] - order[b.impacto];
          })
          .map((flag, i) => {
            const impactoCls = flag.impacto === "alto"
              ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
              : flag.impacto === "medio"
              ? "border-amber-500/30 bg-amber-500/8 text-amber-300"
              : "border-border/30 bg-muted/20 text-muted-foreground";
            const pillCls = flag.impacto === "alto"
              ? "bg-rose-500/20 text-rose-400 border-rose-500/30"
              : flag.impacto === "medio"
              ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
              : "text-muted-foreground";
            return (
              <div key={i} className={cn("rounded-lg border px-4 py-3", impactoCls)}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold">{FLAG_LABELS[flag.flag] ?? flag.flag}</span>
                  <span className={cn("ml-auto text-[10px] border rounded-full px-2 py-0.5", pillCls)}>
                    {flag.impacto === "alto" ? "Alto impacto" : flag.impacto === "medio" ? "Médio impacto" : "Baixo impacto"}
                  </span>
                </div>
                {flag.evidencia && (
                  <p className="text-xs italic border-l-2 border-current/40 pl-3 leading-relaxed opacity-80">
                    "{flag.evidencia}"
                  </p>
                )}
              </div>
            );
          })}
      </div>
    </Card>
  );
}

// ─── GargaloBlock ─────────────────────────────────────────────────────────────

function GargaloBlock({ gargalo }: { gargalo: Gargalo }) {
  return (
    <Card className="p-4 bg-amber-500/5 border-amber-500/30">
      <header className="mb-2 flex items-center gap-2">
        <Warning weight="duotone" className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-foreground">Gargalo principal</h3>
        <span className="ml-2 text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">
          {STAGE_LABELS[gargalo.etapa as keyof MethodStagesUnified] ?? ETAPA_SHORT[gargalo.etapa] ?? gargalo.etapa}
        </span>
      </header>
      <p className="text-sm text-foreground/90 leading-relaxed">{gargalo.descricao}</p>
      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
        <span className="text-amber-400/80 font-medium">Impacto: </span>
        {gargalo.impacto_na_conversao}
      </p>
    </Card>
  );
}

// ─── AcoesCorretivasBlock ─────────────────────────────────────────────────────

function AcoesCorretivasBlock({ acoes }: { acoes: AcaoCorretiva[] }) {
  if (!acoes.length) return null;

  return (
    <Card className="overflow-hidden bg-primary/5 border-primary/25">
      <div className="px-4 py-3 border-b border-primary/15 flex items-center gap-2">
        <Lightning weight="duotone" className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Ações corretivas</h3>
      </div>
      <div className="divide-y divide-primary/10">
        {acoes.map((acao, i) => (
          <div key={i} className={cn("px-4 py-3", i === 0 && "bg-primary/5")}>
            <div className="flex items-start gap-3 border-b border-primary/10 pb-2 mb-2">
              <span className="w-7 h-7 rounded-full bg-primary/20 text-primary font-bold text-sm flex items-center justify-center shrink-0">
                {acao.prioridade}
              </span>
              <div className="flex-1 min-w-0">
                {i === 0 && (
                  <span className="text-[10px] uppercase tracking-wider text-primary font-bold">PRIORIDADE</span>
                )}
                <p className="text-sm font-semibold text-foreground leading-tight">{acao.problema}</p>
                <span className="text-[10px] font-mono text-muted-foreground">
                  {STAGE_LABELS[acao.etapa as keyof MethodStagesUnified] ?? ETAPA_SHORT[acao.etapa] ?? acao.etapa}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Lightning weight="bold" className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground leading-relaxed">{acao.acao}</p>
                {acao.impacto_estimado && (
                  <p className="text-xs text-emerald-400 mt-1">{acao.impacto_estimado}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── SectionList (narrative cards) ───────────────────────────────────────────

interface SectionListProps {
  title: string;
  items: Array<string | NarrativeItem>;
  tone:  "emerald" | "amber" | "primary";
  icon:  typeof CheckCircle;
}

function SectionList({ title, items, tone, icon: Icon }: SectionListProps) {
  if (!items?.length) return null;

  const toneCls =
    tone === "emerald" ? "text-emerald-400" : tone === "amber" ? "text-amber-400" : "text-primary";

  return (
    <Card className="p-4 bg-card/80 border-border/50">
      <header className="mb-3 flex items-center gap-2">
        <Icon weight="duotone" className={cn("w-4 h-4", toneCls)} />
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </header>
      <div className="space-y-2">
        {items.map((item, i) => {
          const text  = extractText(item);
          const etapa = extractEtapa(item);
          const seq   = extractSeq(item);
          return (
            <div key={i} className={cn(
              "rounded-lg border px-4 py-3 flex items-start gap-3",
              tone === "emerald" ? "border-emerald-500/20 bg-emerald-500/5" :
              tone === "amber"   ? "border-amber-500/20 bg-amber-500/5" :
                                   "border-primary/20 bg-primary/5"
            )}>
              <Icon weight="duotone" className={cn("w-5 h-5 shrink-0 mt-0.5", toneCls)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground leading-relaxed">{text}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {etapa && etapa !== "geral" && <EtapaTag etapa={etapa} />}
                  {seq != null && (
                    <a
                      href={`#msg-${seq}`}
                      className="inline-flex items-center text-[10px] font-mono text-primary/60 hover:text-primary transition-colors"
                    >
                      #{seq}
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── PraticaCard ──────────────────────────────────────────────────────────────

function PraticaCard({ pratica }: { pratica: SimulatorReport["pratica"] }) {
  if (!pratica) return null;
  const hasTreinar = pratica.treinar.length > 0;
  const hasFoco    = Boolean(pratica.foco_proxima_sessao);
  if (!hasTreinar && !hasFoco) return null;

  return (
    <Card className="p-4 bg-card/80 border-2 border-primary/40">
      <header className="mb-3 flex items-center gap-2">
        <Target weight="duotone" className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Plano de Prática</h3>
      </header>
      <div className="space-y-3">
        {hasTreinar && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">
              Treinar
            </p>
            <ul className="space-y-1.5">
              {pratica.treinar.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {hasFoco && (
          <div className="flex items-center gap-3 rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5">
            <Target weight="bold" className="w-4 h-4 text-primary shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Foco da próxima sessão
              </p>
              <p className="text-sm font-medium text-foreground leading-tight">
                {pratica.foco_proxima_sessao}
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Verdict badge ────────────────────────────────────────────────────────────

// ─── EneatypeAdaptationBlock ──────────────────────────────────────────────────

function EneatypeAdaptationBlock({
  detected,
  adaptation,
}: {
  detected?: EneatypeDetected;
  adaptation?: EneatypeAdaptation;
}) {
  if (!adaptation && !detected) return null;

  const typeName = adaptation?.type_name ?? detected?.type_name;
  const centro =
    adaptation?.centro_inteligencia ?? detected?.centro_inteligencia;

  const score = adaptation?.score ?? null;
  const scoreCls =
    score == null
      ? "text-muted-foreground"
      : score >= 8
      ? "text-emerald-400"
      : score >= 5
      ? "text-amber-400"
      : "text-rose-400";

  return (
    <Card className="p-4 sm:p-5 bg-card/80 border-indigo-500/30">
      <div className="flex flex-wrap items-start gap-3 justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-1">
            Adaptação ao Código Interno
          </p>
          {typeName && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-lg font-bold text-foreground">{typeName}</p>
              {centro && (
                <Badge className="text-[10px] border bg-slate-500/15 text-slate-300 border-slate-500/30">
                  centro {centro}
                </Badge>
              )}
            </div>
          )}
        </div>
        {score != null && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Score adaptação
            </p>
            <p className={cn("text-2xl font-bold", scoreCls)}>{score}/10</p>
          </div>
        )}
      </div>

      {adaptation && (
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          {adaptation.ajustes_corretos?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1">
                Ajustes corretos
              </p>
              <ul className="space-y-1">
                {adaptation.ajustes_corretos.map((a, i) => (
                  <li key={i} className="text-xs text-foreground/85 flex gap-1.5">
                    <CheckCircle className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {adaptation.ajustes_ausentes?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mb-1">
                Ajustes ausentes
              </p>
              <ul className="space-y-1">
                {adaptation.ajustes_ausentes.map((a, i) => (
                  <li key={i} className="text-xs text-foreground/85 flex gap-1.5">
                    <XCircle className="w-3 h-3 mt-0.5 text-rose-400 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {adaptation?.deal_killers_acionados && adaptation.deal_killers_acionados.length > 0 && (
        <div className="mb-3 p-2.5 rounded-md bg-rose-600/15 border border-rose-600/40">
          <p className="text-[10px] uppercase tracking-wider text-rose-300 font-semibold mb-1">
            Deal-killers acionados pra esse Código
          </p>
          <ul className="space-y-0.5">
            {adaptation.deal_killers_acionados.map((k, i) => (
              <li key={i} className="text-xs text-rose-100 flex gap-1.5">
                <span className="text-rose-400 shrink-0">⚠</span>
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {adaptation?.conducao_descarrilamento?.houve_descarrilamento && (
        <div className="mb-3 p-2.5 rounded-md bg-purple-500/10 border border-purple-500/30">
          <p className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-1">
            Descarrilamento típico do tipo
          </p>
          <p className="text-xs text-foreground/90 mb-1">
            <span className="text-purple-300 font-medium">Lidou:</span>{" "}
            {adaptation.conducao_descarrilamento.como_o_profissional_lidou}
          </p>
          <p className="text-xs text-foreground/90">
            <span className="text-purple-300 font-medium">Técnica oficial:</span>{" "}
            {adaptation.conducao_descarrilamento.tecnica_oficial_que_caberia}
          </p>
        </div>
      )}

      {adaptation?.tecnica_fechamento && (
        <div className={cn(
          "mb-3 p-2.5 rounded-md border",
          adaptation.tecnica_fechamento.match
            ? "bg-emerald-500/10 border-emerald-500/30"
            : "bg-amber-500/10 border-amber-500/30"
        )}>
          <p className={cn(
            "text-[10px] uppercase tracking-wider font-semibold mb-1",
            adaptation.tecnica_fechamento.match ? "text-emerald-400" : "text-amber-400"
          )}>
            Técnica de fechamento {adaptation.tecnica_fechamento.match ? "(match)" : "(mismatch)"}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Ideal:</span>{" "}
              <span className="font-medium text-foreground capitalize">
                {adaptation.tecnica_fechamento.ideal_para_tipo.replace("_", "/")}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Usada:</span>{" "}
              <span className="font-medium text-foreground capitalize">
                {adaptation.tecnica_fechamento.usada_pelo_profissional.replace("_", "/")}
              </span>
            </div>
          </div>
        </div>
      )}

      {adaptation?.stage_adaptation_breakdown && (
        <div className="mb-3 p-2.5 rounded-md bg-indigo-500/5 border border-indigo-500/20">
          <p className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-2">
            Adaptação por etapa
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            {(["conexao","perguntas_iniciais","investigacao_dor","diagnostico_clinico","oferta","fechamento"] as const).map((slug) => {
              const v = adaptation.stage_adaptation_breakdown![slug];
              const labels: Record<string,string> = {
                conexao:"Conexão", perguntas_iniciais:"Perguntas",
                investigacao_dor:"Dor", diagnostico_clinico:"Diagnóstico",
                oferta:"Oferta", fechamento:"Fechamento",
              };
              const cls =
                v >= 8 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : v >= 5 ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                : "bg-rose-500/15 text-rose-300 border-rose-500/30";
              return (
                <div key={slug} className={cn("text-center rounded-md border px-1 py-1.5", cls)}>
                  <p className="text-[9px] uppercase tracking-wider opacity-70">{labels[slug]}</p>
                  <p className="text-sm font-bold">{v}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {adaptation?.insight_personalizado && (
        <div className="p-2.5 rounded-md bg-amber-500/10 border border-amber-500/30 mb-2">
          <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1">
            Insight personalizado
          </p>
          <p className="text-sm text-foreground">{adaptation.insight_personalizado}</p>
        </div>
      )}

      {adaptation?.frase_modelo && (
        <div className="p-2.5 rounded-md bg-primary/5 border border-primary/30">
          <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1">
            Frase modelo pra esse tipo
          </p>
          <p className="text-sm italic text-foreground">"{adaptation.frase_modelo}"</p>
        </div>
      )}
    </Card>
  );
}

function VerdictBadge({ verdict }: { verdict: SimulatorReport["verdict"] }) {
  if (verdict === "accept") {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/20">
        <CheckCircle weight="fill" className="w-3.5 h-3.5 mr-1" />
        Fechou
      </Badge>
    );
  }
  if (verdict === "decline") {
    return (
      <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40 hover:bg-rose-500/20">
        <XCircle weight="fill" className="w-3.5 h-3.5 mr-1" />
        Não fechou
      </Badge>
    );
  }
  if (verdict === "delay") {
    return (
      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/20">
        <Clock weight="fill" className="w-3.5 h-3.5 mr-1" />
        Adiou — vai pensar
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <Clock weight="fill" className="w-3.5 h-3.5 mr-1" />
      Encerrado sem fechamento
    </Badge>
  );
}

// ─── TranscriptSection ────────────────────────────────────────────────────────

export type TranscriptEntry = { seq: number; role: "user" | "patient" | "hint"; content: string };

function TranscriptSection({ entries }: { entries: TranscriptEntry[] }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!entries.length) return null;

  return (
    <Card className="bg-card/80 border-border/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/20 transition text-left"
      >
        <ChatCircle weight="duotone" className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold text-foreground flex-1">
          Transcrição Completa
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            ({entries.length} mensagens)
          </span>
        </span>
        <CaretDown
          weight="bold"
          className={cn(
            "w-3 h-3 text-muted-foreground transition-transform duration-150 shrink-0",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div ref={containerRef} className="px-4 pb-4 border-t border-border/30 pt-3 space-y-2 max-h-[60vh] overflow-y-auto">
          {entries.map((msg) => {
            if (msg.role === "hint") {
              return (
                <div
                  key={msg.seq}
                  id={`msg-${msg.seq}`}
                  className="flex justify-center"
                >
                  <div className="max-w-[85%] text-center">
                    <span className="text-[10px] font-mono text-amber-400/60 block mb-0.5">
                      Coach #{msg.seq}
                    </span>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                      <p className="text-xs text-amber-300/90 leading-relaxed italic">{msg.content}</p>
                    </div>
                  </div>
                </div>
              );
            }
            if (msg.role === "patient") {
              return (
                <div
                  key={msg.seq}
                  id={`msg-${msg.seq}`}
                  className="flex justify-start"
                >
                  <div className="max-w-[80%]">
                    <span className="text-[10px] font-mono text-muted-foreground/60 block mb-0.5 pl-1">
                      Paciente #{msg.seq}
                    </span>
                    <div className="bg-muted/30 border border-border/30 rounded-2xl rounded-tl-sm px-3 py-2">
                      <p className="text-sm text-foreground/80 leading-relaxed">{msg.content}</p>
                    </div>
                  </div>
                </div>
              );
            }
            // role === "user"
            return (
              <div
                key={msg.seq}
                id={`msg-${msg.seq}`}
                className="flex justify-end"
              >
                <div className="max-w-[80%]">
                  <span className="text-[10px] font-mono text-primary/60 block mb-0.5 text-right pr-1">
                    Você #{msg.seq}
                  </span>
                  <div className="bg-primary/15 border border-primary/25 rounded-2xl rounded-tr-sm px-3 py-2">
                    <p className="text-sm text-foreground leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export interface SimuladorReportProps {
  report: SimulatorReport;
  transcript?: TranscriptEntry[] | null;
  onBack?: () => void;
   
  [key: string]: any;
}

export function SimuladorReport({ report, transcript, onBack }: SimuladorReportProps) {
  return (
    <div className="space-y-6" data-testid="simulador-report">

      {/* ── Hero ── */}
      <Card className="p-6 lg:p-8 bg-card/80 border-border/50">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex items-center gap-5">
            <div className="flex flex-col items-center justify-center rounded-2xl border border-primary/40 px-6 py-4 bg-primary/10">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Score final
              </span>
              <span
                className="text-5xl font-bold tabular-nums text-primary"
                data-testid="report-final-score"
              >
                {report.final_score}
              </span>
              <span className="text-[10px] text-muted-foreground">de 100</span>
            </div>
            <div className="space-y-2">
              <VerdictBadge verdict={report.verdict} />
              <p className="text-xs text-muted-foreground">
                Método: {report.method_total ?? 0}/60
              </p>
            </div>
          </div>

          {onBack && (
            <div className="flex flex-col gap-2 self-start ml-auto">
              <button
                type="button"
                onClick={onBack}
                className="text-sm text-muted-foreground hover:text-foreground transition"
              >
                Voltar
              </button>
            </div>
          )}
        </div>

        {/* Resumo executivo */}
        {report.resumo_executivo && (
          <div className="mt-4 bg-muted/20 border border-border/30 rounded-lg p-4">
            <p className="text-sm text-foreground leading-relaxed">{report.resumo_executivo}</p>
          </div>
        )}
      </Card>

      {/* ── Adaptação ao eneatipo ── */}
      <EneatypeAdaptationBlock
        detected={report.eneatype_detected}
        adaptation={report.eneatype_adaptation}
      />

      {/* ── Método Influência Máxima — 6 Etapas ── */}
      <MethodStagesSection report={report} />

      {/* ── Flags críticas ── */}
      {report.flags_criticas?.some(f => f.detectada) && (
        <FlagsDetectadasBlock flags={report.flags_criticas} />
      )}

      {/* ── Gargalo principal ── */}
      {report.gargalo_principal && (
        <GargaloBlock gargalo={report.gargalo_principal} />
      )}

      {/* ── Ações corretivas ── */}
      {report.acoes_corretivas?.length > 0 && (
        <AcoesCorretivasBlock acoes={report.acoes_corretivas} />
      )}

      {/* ── Two-column body ── */}
      <div className="grid lg:grid-cols-2 gap-6">

        {/* Left column */}
        <div className="space-y-4">
          {/* Prática vem primeiro */}
          <PraticaCard pratica={report.pratica} />

          <SectionList
            title="O que foi bem"
            items={report.strengths}
            tone="emerald"
            icon={CheckCircle}
          />
          <SectionList
            title="O que pode melhorar"
            items={report.improvements}
            tone="amber"
            icon={Warning}
          />
          {report.recommendations?.length ? (
            <SectionList
              title="Próximas vezes"
              items={report.recommendations}
              tone="primary"
              icon={Lightbulb}
            />
          ) : null}
        </div>

        {/* Right column — key moments + palavra emocional + dor */}
        <div className="space-y-4">
          {/* Palavra emocional central */}
          {report.palavra_emocional_central && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <span className="text-xs text-violet-400 font-medium">Palavra-chave:</span>
              <span className="text-sm font-semibold text-violet-300">
                {report.palavra_emocional_central}
              </span>
            </div>
          )}

          {/* Nível de dor */}
          {report.nivel_dor_maximo && (
            <Card className="p-4 bg-card/80 border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">
                Nível de dor atingido
              </p>
              <p className="text-sm font-semibold text-foreground">
                Nível {report.nivel_dor_maximo.nivel} —{" "}
                {["", "Fato", "Sentimento", "Privação"][report.nivel_dor_maximo.nivel]}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{report.nivel_dor_maximo.descricao}</p>
              {report.nivel_dor_maximo.evidencia && (
                <p className="text-xs italic text-foreground/70 mt-1 border-l-2 border-border/40 pl-2">
                  "{report.nivel_dor_maximo.evidencia}"
                </p>
              )}
            </Card>
          )}

          {/* Key moments */}
          {report.key_moments?.length ? (
            <Card className="p-4 bg-card/80 border-border/50">
              <header className="mb-3 flex items-center gap-2">
                <Lightning weight="duotone" className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Momentos-chave</h3>
              </header>
              <ul className="space-y-2">
                {report.key_moments.map((km, idx) => {
                  const KMIcon = KEY_MOMENT_ICON[km.type];
                  return (
                    <li key={`${km.seq}-${idx}`}>
                      <div className="w-full text-left rounded-lg border border-border/40 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <KMIcon
                            weight="duotone"
                            className={cn("w-4 h-4", KEY_MOMENT_TONE[km.type])}
                          />
                          <span className="text-sm font-medium text-foreground">{km.label}</span>
                          <a
                            href={`#msg-${km.seq}`}
                            className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                          >
                            #{km.seq}
                          </a>
                        </div>
                        {km.excerpt ? (
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                            "{km.excerpt}"
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>

      {/* ── Transcrição Completa ── */}
      {transcript && transcript.length > 0 && (
        <TranscriptSection entries={transcript} />
      )}
    </div>
  );
}
